import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MessageService } from './message.service';

export interface Partenaire {
  id?: number;
  immatriculation: string;
  marque: string;
  modele: string;
  couleur?: string;
  numeroChassis: string;
  enService: boolean;
  disponible: boolean;
  typeFlotte: string;        // toujours 'PARTENAIRE'
  nomProprietaire: string;
  contactProprietaire: string;
  typeContrat: string;       // 'PAR_LIVRAISON' | 'MENSUEL' | 'HYBRIDE'
  montantParLivraison?: number;
  montantMensuel?: number;
  livraisonsMois?: number;
  montantGenereMois?: number;
  actif?: boolean;
  dateCreation?: string;
  dateModification?: string;
}

@Injectable({ providedIn: 'root' })
export class PartenaireService {

  private readonly base = `${environment.apiurl}vehicules`;

  constructor(
    private http: HttpClient,
    private messageService: MessageService
  ) {}

  /** Liste tous les partenaires */
  listerPartenaires(): Observable<Partenaire[]> {
    return this.http.get<Partenaire[]>(`${this.base}/partenaires`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Un seul partenaire par ID */
  obtenirPartenaire(id: number): Observable<Partenaire> {
    return this.http.get<Partenaire>(`${this.base}/${id}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Créer un partenaire (force typeFlotte=PARTENAIRE) */
  creerPartenaire(data: Partial<Partenaire>): Observable<Partenaire> {
    return this.http.post<Partenaire>(this.base, { ...data, typeFlotte: 'PARTENAIRE' })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Modifier un partenaire */
  modifierPartenaire(id: number, data: Partial<Partenaire>): Observable<Partenaire> {
    return this.http.put<Partenaire>(`${this.base}/${id}`, { ...data, typeFlotte: 'PARTENAIRE' })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Supprimer un partenaire */
  supprimerPartenaire(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Activer / désactiver le service */
  changerStatutService(id: number, enService: boolean): Observable<Partenaire> {
    return this.http.patch<Partenaire>(`${this.base}/${id}/statut`, null, { params: { enService: String(enService) } })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Enregistrer des livraisons supplémentaires */
  enregistrerLivraisons(id: number, nombre: number = 1): Observable<Partenaire> {
    return this.http.patch<Partenaire>(`${this.base}/${id}/partenaires/enregistrer-livraison`, null, { params: { nombreLivraisons: String(nombre) } })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Clôturer le mois (remet les compteurs à zéro) */
  cloturerMois(id: number): Observable<Partenaire> {
    return this.http.patch<Partenaire>(`${this.base}/${id}/partenaires/cloture-mensuelle`, null)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Tableau de bord global partenaires */
  tableauBord(): Observable<any> {
    return this.http.get<any>(`${this.base}/partenaires/tableau-bord`)
      .pipe(catchError(this.messageService.gererErreur));
  }
}
